export * from './app-names';
export * from './enums';
export * from './marketplace';
/**
 * Constants for HOME team
 * @external home-constants
 * @example
 * import { LOAN_PURPOSE_REFINANCE } from '@nerdwallet/home-constants';
 *
 * doSomeHomeThing({
 *   loanPurpose: LOAN_PURPOSE_REFINANCE,
 *   ...otherStuff
 * });
 */

/**
 * @typedef AGENT_PURPOSE
 * @memberof external:home-constants
 * @prop {String} AGENT_PURPOSE_BUY  User is looking for a buyer's agent
 * @prop {String} AGENT_PURPOSE_SELL  User is looking for a seller's agent (may also be buying)
 * @example
 * import {
 *   AGENT_PURPOSE_BUY,
 *   AGENT_PURPOSE_BUY
 * } from '@nerdwallet/home-constants';
 */

export var AGENT_PURPOSE_BUY = 'buyer';
export var AGENT_PURPOSE_SELL = 'seller';
export var AGENT_PURPOSE = [AGENT_PURPOSE_BUY, AGENT_PURPOSE_SELL];
/**
 * @typedef LOAN_PURPOSE
 * @memberof external:home-constants
 * @prop {String} LOAN_PURPOSE_PURCHASE  Purchase
 * @prop {String} LOAN_PURPOSE_REFINANCE  Refinance
 * @example
 * import {
 *   LOAN_PURPOSE_PURCHASE,
 *   LOAN_PURPOSE_REFINANCE
 * } from '@nerdwallet/home-constants';
 */

export var LOAN_PURPOSE_PURCHASE = 'purchase';
export var LOAN_PURPOSE_REFINANCE = 'refinance';
export var LOAN_PURPOSE = [LOAN_PURPOSE_PURCHASE, LOAN_PURPOSE_REFINANCE];
export var LOAN_PROGRAM_FIXED_30 = '30-year-fixed';
export var LOAN_PROGRAM_FIXED_20 = '20-year-fixed';
export var LOAN_PROGRAM_FIXED_15 = '15-year-fixed';
export var LOAN_PROGRAM_FIXED_10 = '10-year-fixed';
export var LOAN_PROGRAM_ARM_7_1 = '7-1-arm';
export var LOAN_PROGRAM_ARM_5_1 = '5-1-arm';
export var LOAN_PROGRAM_ARM_3_1 = '3-1-arm';
/**
 * @typedef LOAN_PROGRAM
 * @memberof external:home-constants
 * @property {String} LOAN_PROGRAM_FIXED_30 30-year fixed mortgage
 * @property {String} LOAN_PROGRAM_FIXED_20 20-year fixed mortgage
 * @property {String} LOAN_PROGRAM_FIXED_15 15-year fixed mortgage
 * @property {String} LOAN_PROGRAM_FIXED_10 10-year fixed mortgage
 * @property {String} LOAN_PROGRAM_ARM_7_1 7/1 adustable rate mortgage
 * @property {String} LOAN_PROGRAM_ARM_5_1 5/1 adustable rate mortgage
 * @property {String} LOAN_PROGRAM_ARM_3_1 3/1 adustable rate mortgage
 * @example
 * import {
 *   LOAN_PROGRAM_FIXED_30,
 *   LOAN_PROGRAM_FIXED_20,
 *   LOAN_PROGRAM_FIXED_15,
 *   LOAN_PROGRAM_FIXED_10,
 *   LOAN_PROGRAM_ARM_7_1,
 *   LOAN_PROGRAM_ARM_5_1,
 *   LOAN_PROGRAM_ARM_3_1
 * } from '@nerdwallet/home-constants';
 */

export var LOAN_PROGRAM = [LOAN_PROGRAM_FIXED_30, LOAN_PROGRAM_FIXED_20, LOAN_PROGRAM_FIXED_15, LOAN_PROGRAM_FIXED_10, LOAN_PROGRAM_ARM_7_1, LOAN_PROGRAM_ARM_5_1, LOAN_PROGRAM_ARM_3_1];
export var LOAN_TYPE_CONVENTIONAL = 'conventional';
export var LOAN_TYPE_FHA = 'fha';
export var LOAN_TYPE_VA = 'va';
export var LOAN_TYPE_USDA = 'usda';
export var LOAN_TYPE_JUMBO = 'jumbo';
/**
 * @typedef LOAN_TYPE
 * @memberof external:home-constants
 * @property {String} LOAN_TYPE_CONVENTIONAL A conventional loan
 * @property {String} LOAN_TYPE_FHA FHA loan; a mortgage insured by Federal Housing Administration
 * @property {string} LOAN_TYPE_JUMBO Non-conforming "Jumbo" loan
 * @property {String} LOAN_TYPE_USDA USDA loan; insured, guaranteed or issued by the US Dept. of Agriculture
 * @property {String} LOAN_TYPE_VA VA loan; a mortgage guaranteed by US Dept. of Veteran's Affairs
 * @example
 * import {
 *   LOAN_TYPE_CONVENTIONAL,
 *   LOAN_TYPE_FHA,
 *   LOAN_TYPE_VA,
 *   LOAN_TYPE_USDA
 * } from '@nerdwallet/home-constants';
 */

export var LOAN_TYPE = [LOAN_TYPE_CONVENTIONAL, LOAN_TYPE_FHA, LOAN_TYPE_JUMBO, LOAN_TYPE_USDA, LOAN_TYPE_VA];
/**
 * @typedef PROPERTY_USE
 * @memberof external:home-constants
 * @property {String} PROPERTY_USE_PRIMARY The property is their primary residence
 * @property {String} PROPERTY_USE_SECONDARY The property is a secondary or vacation home
 * @property {String} PROPERTY_USE_INVESTMENT They do not reside in the property
 * @example
 * import {
 *   PROPERTY_USE_PRIMARY,
 *   PROPERTY_USE_SECONDARY,
 *   PROPERTY_USE_INVESTMENT,
 * } from '@nerdwallet/home-constants';
 */

export var PROPERTY_USE_PRIMARY = 'primary';
export var PROPERTY_USE_SECONDARY = 'secondary';
export var PROPERTY_USE_INVESTMENT = 'investment';
export var PROPERTY_USE = [PROPERTY_USE_PRIMARY, PROPERTY_USE_SECONDARY, PROPERTY_USE_INVESTMENT];
/**
 * @typedef PROPERTY_TYPE
 * @memberof external:home-constants
 * @property {String} PROPERTY_TYPE_HOUSE Single family home
 * @property {String} PROPERTY_TYPE_TOWNHOME Townhouse
 * @property {String} PROPERTY_TYPE_CONDO Condominium
 * @property {String} PROPERTY_TYPE_MULTI_FAMILY Multi-family building, i.e. an apartment
 * @example
 * import {
 *   PROPERTY_TYPE_HOUSE,
 *   PROPERTY_TYPE_TOWNHOME,
 *   PROPERTY_TYPE_CONDO,
 *   PROPERTY_TYPE_MULTI_FAMILY,
 * } from '@nerdwallet/home-constants';
 */

export var PROPERTY_TYPE_HOUSE = 'house';
export var PROPERTY_TYPE_TOWNHOME = 'townhome';
export var PROPERTY_TYPE_CONDO = 'condo';
export var PROPERTY_TYPE_MULTI_FAMILY = 'multi-family';
export var PROPERTY_TYPE_OTHER = 'other';
export var PROPERTY_TYPE = [PROPERTY_TYPE_HOUSE, PROPERTY_TYPE_TOWNHOME, PROPERTY_TYPE_CONDO, PROPERTY_TYPE_MULTI_FAMILY];
/**
 * @typedef RATE_TYPE
 * @memberof external:home-constants
 * @property {String} RATE_TYPE_FIXED_30 30-year fixed mortgage
 * @property {String} RATE_TYPE_FIXED_30_FHA 30-year fixed fha mortgage
 * @property {String} RATE_TYPE_FIXED_30_VA 30-year fixed va mortgage
 * @property {String} RATE_TYPE_FIXED_30_JUMBO 30-year fixed jumbo mortgage
 * @property {String} RATE_TYPE_FIXED_20 20-year fixed mortgage
 * @property {String} RATE_TYPE_FIXED_15 15-year fixed mortgage
 * @property {String} RATE_TYPE_FIXED_15_JUMBO 15-year fixed jumbo mortgage
 * @property {String} RATE_TYPE_FIXED_10 10-year fixed mortgage
 * @property {String} RATE_TYPE_ARM_10_1 10/1 adustable rate mortgage
 * @property {String} RATE_TYPE_ARM_7_1 7/1 adustable rate mortgage
 * @property {String} RATE_TYPE_ARM_5_1_JUMBO 5/1 adustable rate jumbo mortgage
 * @property {String} RATE_TYPE_ARM_5_1 5/1 adustable rate mortgage
 * @property {String} RATE_TYPE_ARM_3_1 3/1 adustable rate mortgage
 * @example
 * import {
 *   RATE_TYPE_FIXED_30,
 *   RATE_TYPE_FIXED_30_FHA,
 *   RATE_TYPE_FIXED_30_VA,
 *   RATE_TYPE_FIXED_30_FHA,
 *   RATE_TYPE_FIXED_30_VA,
 *   RATE_TYPE_FIXED_30_JUMBO,
 *   RATE_TYPE_FIXED_20,
 *   RATE_TYPE_FIXED_15,
 *   RATE_TYPE_FIXED_10,
 *   RATE_TYPE_ARM_10_1,
 *   RATE_TYPE_ARM_7_1,
 *   RATE_TYPE_ARM_5_1,
 *   RATE_TYPE_ARM_5_1_JUMBO,
 *   RATE_TYPE_ARM_3_1,
 * } from '@nerdwallet/home-constants';
 */

export var RATE_TYPE_FIXED_30 = 'fixed_30';
export var RATE_TYPE_FIXED_30_FHA = 'fixed_30_fha';
export var RATE_TYPE_FIXED_30_VA = 'fixed_30_va';
export var RATE_TYPE_FIXED_30_JUMBO = 'fixed_30_jumbo';
export var RATE_TYPE_FIXED_20 = 'fixed_20';
export var RATE_TYPE_FIXED_15 = 'fixed_15';
export var RATE_TYPE_FIXED_15_JUMBO = 'fixed_15_jumbo';
export var RATE_TYPE_FIXED_10 = 'fixed_10';
export var RATE_TYPE_ARM_10_1 = 'arm_10_1';
export var RATE_TYPE_ARM_7_1 = 'arm_7_1';
export var RATE_TYPE_ARM_5_1_JUMBO = 'arm_5_1_jumbo';
export var RATE_TYPE_ARM_5_1 = 'arm_5_1';
export var RATE_TYPE_ARM_3_1 = 'arm_3_1';
export var RATE_TYPE = [RATE_TYPE_FIXED_30, RATE_TYPE_FIXED_30_FHA, RATE_TYPE_FIXED_30_VA, RATE_TYPE_FIXED_30_JUMBO, RATE_TYPE_FIXED_20, RATE_TYPE_FIXED_15_JUMBO, RATE_TYPE_FIXED_15, RATE_TYPE_FIXED_10, RATE_TYPE_ARM_10_1, RATE_TYPE_ARM_7_1, RATE_TYPE_ARM_5_1, RATE_TYPE_ARM_5_1_JUMBO, RATE_TYPE_ARM_3_1];
/**
 * @typedef USER_GOAL
 * @memberof external:home-constants
 * @property {String} USER_GOAL_LOWEST_MONTHLY_PAYMENT User is trying to lower their monthly payment
 * @property {String} USER_GOAL_LOWEST_INTEREST_RATE User is trying lower the total cost of their
 * loan (inaccurately named)
 * @example
 * import {
 *   USER_GOAL_LOWEST_MONTHLY_PAYMENT,
 *   USER_GOAL_LOWEST_INTEREST_RATE,
 * } from '@nerdwallet/home-constants';
 */

export var USER_GOAL_LOWEST_MONTHLY_PAYMENT = 'lowestMonthlyPayment';
export var USER_GOAL_LOWEST_INTEREST_RATE = 'lowestInterestRate';
export var USER_GOALS = [USER_GOAL_LOWEST_MONTHLY_PAYMENT, USER_GOAL_LOWEST_INTEREST_RATE];
/**
 * @typedef VETERAN_TYPE
 * @memberof external:home-constants
 * @property {String} VETERAN_MILITARY User is/was member of the military
 * @property {String} VETERAN_NATIONAL_GUARD User is/was a member of the National Guard or Reserves
 * @example
 * import {
 *   VETERAN_TYPE_MILITARY,
 *   VETERAN_TYPE_NATIONAL_GUARD,
 * } from '@nerdwallet/home-constants';
 */

export var VETERAN_TYPE_MILITARY = 'military';
export var VETERAN_TYPE_NATIONAL_GUARD = 'guard';
export var VETERAN_TYPE = [VETERAN_TYPE_MILITARY, VETERAN_TYPE_NATIONAL_GUARD];
/**
 * @typedef HOME_BUYING_PROCESS
 * @memberof external:home-constants
 * @property {String} HOME_BUYING_PROCESS_JUST_STARTING user is just starting their home buying process
 * @property {String} HOME_BUYING_PROCESS_NARROWING_OPTIONS user is narrowing their options
 * @property {String} HOME_BUYING_PROCESS_CONSIDERING_HOMES user is shopping some homes
 * @property {String} HOME_BUYING_PROCESS_MAKING_OFFER user is making an offer for a house
 * @property {String} HOME_BUYING_PROCESS_UNDER_CONTRACT user is under contract with an agent
 * @example
 * import {
 *   HOME_BUYING_PROCESS_JUST_STARTING,
 *   HOME_BUYING_PROCESS_NARROWING_OPTIONS,
 *   HOME_BUYING_PROCESS_CONSIDERING_HOMES,
 *   HOME_BUYING_PROCESS_MAKING_OFFER,
 *   HOME_BUYING_PROCESS_UNDER_CONTRACT,
 * } from '@nerdwallet/home-constants';
 */

export var HOME_BUYING_PROCESS_JUST_STARTING = 'JUST_STARTING';
export var HOME_BUYING_PROCESS_NARROWING_OPTIONS = 'NARROWING_OPTIONS';
export var HOME_BUYING_PROCESS_CONSIDERING_HOMES = 'CONSIDERING_HOMES';
export var HOME_BUYING_PROCESS_MAKING_OFFER = 'MAKING_OFFER';
export var HOME_BUYING_PROCESS_UNDER_CONTRACT = 'UNDER_CONTRACT';
export var HOME_BUYING_PROCESS = [HOME_BUYING_PROCESS_JUST_STARTING, HOME_BUYING_PROCESS_NARROWING_OPTIONS, HOME_BUYING_PROCESS_CONSIDERING_HOMES, HOME_BUYING_PROCESS_MAKING_OFFER, HOME_BUYING_PROCESS_UNDER_CONTRACT];
export var MORTECH_MOCK_HEADER = 'x-nw-offer-mock';
export var MORTECH_MOCK_QUERY = 'nw_offer_mock';
export var REDIRECT_PARTNER_PARAM = 'utm_campaign';